import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FiSearch } from "react-icons/fi";

function MainPage() {
  const [data, setData] = useState([]);
  const [localInput, setLocalInput] = useState("");
  const [availableNumbers, setAvailableNumbers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const accountSid = process.env.REACT_APP_TWILIO_ACCOUNT_SID;
  const authToken = process.env.REACT_APP_TWILIO_AUTH_TOKEN;
  // detect screen size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640); // sm breakpoint
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const basicAuth = btoa(`${accountSid}:${authToken}`);
  const myHeaders = new Headers();
  myHeaders.append("Authorization", `Basic ${basicAuth}`);
  const dialCodeMap = {
    GR: "+30", // Greece
    DE: "+49", // Germany
    IT: "+39", // Italy
    JP: "+81", // Japan
    EE: "+372", // Estonia
    DK: "+45", // Denmark
    BG: "+359", // Bulgaria
    MY: "+60", // Malaysia
    CL: "+56", // Chile
    BR: "+55", // Brazil
    TH: "+66", // Thailand
    PT: "+351", // Portugal
    RO: "+40", // Romania
    GB: "+44", // United Kingdom
    AR: "+54", // Argentina
    AT: "+43", // Austria
    BE: "+32", // Belgium
    CO: "+57", // Colombia
    HR: "+385", // Croatia
    FI: "+358", // Finland
    ID: "+62", // Indonesia
    IL: "+972", // Israel
    MX: "+52", // Mexico
    PE: "+51", // Peru
    PH: "+63", // Philippines
    PL: "+48", // Poland
    SK: "+421", // Slovakia
    ZA: "+27", // South Africa
    SE: "+46", // Sweden
    TN: "+216", // Tunisia
    NA: "+264", // Namibia
    GN: "+224", // Guinea
    HU: "+36", // Hungary
    IE: "+353", // Ireland
    AU: "+61", // Australia
    LT: "+370", // Lithuania
    CA: "+1", // Canada
    PA: "+507", // Panama
    CZ: "+420", // Czech Republic
    DZ: "+213", // Algeria
    US: "+1", // United States
    VI: "+1-340", // Virgin Islands, U.S.
    EC: "+593", // Ecuador
    KE: "+254", // Kenya
    NL: "+31", // Netherlands
    CH: "+41", // Switzerland
    SI: "+386", // Slovenia
    PR: "+1-787", // Puerto Rico
    NZ: "+64", // New Zealand
    HK: "+852", // Hong Kong
    GD: "+1-473", // Grenada
    GE: "+995", // Georgia
    SV: "+503", // El Salvador
    BJ: "+229", // Benin
    BB: "+1-246", // Barbados
  };

  // Convert vanity letters → digits
  const letterToDigit = (word) => {
    const map = {
      A: 2,
      B: 2,
      C: 2,
      D: 3,
      E: 3,
      F: 3,
      G: 4,
      H: 4,
      I: 4,
      J: 5,
      K: 5,
      L: 5,
      M: 6,
      N: 6,
      O: 6,
      P: 7,
      Q: 7,
      R: 7,
      S: 7,
      T: 8,
      U: 8,
      V: 8,
      W: 9,
      X: 9,
      Y: 9,
      Z: 9,
    };
    return word
      .toUpperCase()
      .split("")
      .map((char) => map[char] || char)
      .join("");
  };
  // Fetch countries list
  const getNumbers = async () => {
    try {
      const response = await fetch(
        `https://api.twilio.com/2010-04-01/Accounts/${accountSid}/AvailablePhoneNumbers.json`,
        { method: "GET", headers: myHeaders }
      );
      const numbers = await response.json();
      const lastNumbers = numbers.countries || [];
      setData(lastNumbers);
    } catch (error) {
      console.log(error);
    }
  };

  // Format number nicely
  const formatPhoneNumber = (num) => {
    if (!num) return "";
    const cleaned = num.replace(/[^\d+]/g, "");
    const match = cleaned.match(/^(\+?\d{1})(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `${match[1]} (${match[2]}) ${match[3]}-${match[4]}`;
    }
    return num;
  };

  // Utility: clean digits only
  // const cleanNumber = (num) => num.replace(/\D/g, "");
  function cleanNumber(num) {
    return num.replace(/\D/g, "");
  }
  // Filter by country + startsWith input
  // Updated filterNumbers: also works for vanity
  function filterNumbers(numbers, selectedCode, cleanInput) {
    const dialCode = dialCodeMap[selectedCode] || "";
    const cleanCode = cleanNumber(dialCode);

    return numbers.filter((num) => {
      const normalized = cleanNumber(num.phone_number);
      if (!normalized.startsWith(cleanCode)) return false;
      const localPart = normalized.slice(cleanCode.length);

      return localPart.startsWith(cleanInput); // matches vanity-converted digits
    });
  }

  // Handle Search
  const handleSubmit = async (e) => {
    setSearchQuery(localInput);

    if (!localInput.trim()) {
      toast.error("Please enter Area Code or Vanity");
      return;
    }

    // ✅ Fix countryCode = "US" always
    const selectedCode = "US";

    setLoading(true);
    setAvailableNumbers([]);
    setHasSearched(true);

    let params = [];

    const rawInput = localInput.replace(/[^a-zA-Z0-9]/g, "");
    const areaPart = rawInput.slice(0, 3);
    const restPart = rawInput.slice(3);

    if (/^\d{3}$/.test(areaPart)) {
      params.push(`AreaCode=${areaPart}`);
    }

    if (restPart) {
      const vanityDigits = letterToDigit(restPart);
      params.push(`Contains=${vanityDigits}`);
    }

    if (/^\d+$/.test(rawInput) && rawInput.length > 3) {
      params = [`Contains=${rawInput}`];
    }

    const apiUrl = `https://api.twilio.com/2010-04-01/Accounts/${accountSid}/AvailablePhoneNumbers/${selectedCode}/Local.json?${params.join(
      "&"
    )}`;

    try {
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: myHeaders,
      });
      const availableNumbers = await response.json();
      let finalNumbers = availableNumbers.available_phone_numbers || [];

      const digitsForFilter = letterToDigit(rawInput);
      finalNumbers = filterNumbers(finalNumbers, selectedCode, digitsForFilter);

      setAvailableNumbers(finalNumbers);
    } catch (error) {
      console.error(error);
    }

    setLoading(false);
  };

  useEffect(() => {
    getNumbers();
  }, []);
  function redirectToUrl() {
    window.location.href =
      "https://link.doorio.com/widget/form/ET82mgBpn6yFUjGm2lNo";
  }

  return (
    <div className="bg-white mt-20">
      <div className="text-center  max-w-4xl mx-auto px-4">
        <h2 className="text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl">
          Search For Your Custom Digital Vanity Number!
        </h2>

        <div className="text-center  max-w-2xl mx-auto px-6">
          {/* ✅ Description */}
          <p className="mt-3 text-lg text-gray-600 dark:text-gray-300">
            Find a unique, memorable number that represents your brand or
            business. Enter an{" "}
            <span className="font-semibold text-[#F6A33C]">area code</span> or a{" "}
            <span className="font-semibold text-[#F6A33C]">keyword</span> to see
            available options instantly.
          </p>
        </div>
        {/* ✅ Instruction Box */}
        <div className="mt-4 max-w-xl mx-auto bg-[#FFF7ED] border border-[#F6A33C] text-yellow-800 px-4 py-2 rounded-md shadow-sm text-sm font-medium text-center">
          📌 Once you find the number, copy it — you will need it on the
          purchase page.
        </div>
      </div>
      <div className="card w-full p-10 bg-white dark:bg-gray-900 rounded-2xl mt-6 ">
        {/* Search Section */}
        <div className="flex items-start mt-6 w-full justify-between mb-[-20px] sm:ml-4">
          {/* Search Input Section (Left Side) */}
          <div className="relative w-full max-w-[450px] ">
            {/* US Prefix */}
            <div className="absolute left-4 top-1/3 sm:top-1/3 -translate-y-1/2 flex items-center cursor-pointer">
              <span className="text-gray-500 font-semibold">US</span>
            </div>

            {/* Input Field */}
            <input
              required
              type="text"
              placeholder="Search Phone Number"
              className="w-full py-4 pl-14 pr-12 rounded-full border border-slate-300 
               focus:outline-none focus:ring-2 focus:ring-[#F6A33C]/70 
               dark:bg-gray-800 dark:text-white dark:border-gray-700 
               shadow-md transition duration-200"
              value={localInput}
              onChange={(e) => setLocalInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSubmit();
                }
              }}
            />

            {/* Search Button */}
            <button
              onClick={handleSubmit}
              className="absolute right-4 top-1/3 sm:top-1/3  -translate-y-1/2 flex items-center 
               justify-center text-gray-500 hover:text-[#F6A33C] transition 
               text-2xl rounded-full"
            >
              <FiSearch />
            </button>

            {/* Example Line */}
            <p className="text-sm text-gray-500 mt-3 md:ml-14 ml-8 ">
              <span className="font-semibold text-[#2C3E50]">Examples:</span>{" "}
              <span>(912) GRAND</span> / <span>916-TAX</span> /{" "}
              <span>916HELP</span>
            </p>
          </div>

          {/* 📱 Phone Keypad Image (Right Side) */}
          <div className="ml-4 flex-shrink-0 px-4 py-4 border mt-[-60px]  bg-[#fdf7f1] rounded-xl shadow-md hidden sm:block opacity-80 mr-9">
            <p className="font-bold mb-2">Mobile Keypad</p>
            <img
              src="/image.jpeg"
              alt="Phone Keypad"
              className="w-28 h-auto object-contain rounded-lg "
            />
          </div>
        </div>
      </div>
      <div className="h-full w-full pb-6 bg-base-100 md:mt-[-20px] mt-7 ">
        <div>
          <div className="text-sm text-gray-500   ml-16">
            {availableNumbers.length > 0 && (
              <div className="dark:text-gray-300">
                Showing results for:{" "}
                <span className="font-semibold text-[#2C3E50]">
                  {searchQuery}
                </span>
              </div>
            )}
          </div>

          <div className="overflow-x-auto w-full px-14 rounded-lg shadow-sm ">
            <table className="w-full table-auto  border-collapse rounded-lg overflow-hidden">
              {availableNumbers.length > 0 && (
                <thead className="bg-[#fdf7f1] h-12 text-[#2C3E50] text-sm uppercase tracking-wide">
                  <tr>
                    {(() => {
                      let columns = 3;
                      if (isMobile) {
                        columns = 1;
                      } else if (availableNumbers.length < 10) {
                        columns = 1;
                      } else if (availableNumbers.length < 20) {
                        columns = 2;
                      }

                      return Array.from({ length: columns }).map((_, i) => (
                        <th
                          key={i}
                          className={`${
                            i < columns - 1 ? "border-r border-gray-200" : ""
                          } ${
                            columns === 1
                              ? "w-full"
                              : columns === 2
                              ? "w-1/2"
                              : "w-1/3"
                          } px-4 text-left font-semibold`}
                        >
                          <div className="flex justify-between items-center">
                            <span>Numbers</span>
                            <span>Actions</span>
                          </div>
                        </th>
                      ));
                    })()}
                  </tr>
                </thead>
              )}
              <tbody className="bg-white">
                {loading ? (
                  <tr>
                    <td colSpan="2" className="text-center py-8">
                      <div className="flex justify-center items-center">
                        <div className="w-8 h-8 border-4 border-[#F8B25E] border-t-transparent rounded-full animate-spin"></div>
                        <span className="ml-3 text-gray-600">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : !hasSearched ? (
                  <tr>
                    <td colSpan="2" className="text-center py-8 text-gray-500">
                      Enter Area Code or Vanity Search
                    </td>
                  </tr>
                ) : hasSearched && availableNumbers.length === 0 ? (
                  <tr>
                    <td colSpan="2" className="text-center py-8 text-gray-500">
                      No numbers found for "{searchQuery}"
                    </td>
                  </tr>
                ) : (
                  (() => {
                    let columns = 3;
                    if (isMobile) {
                      columns = 1;
                    } else if (availableNumbers.length < 10) {
                      columns = 1;
                    } else if (availableNumbers.length < 20) {
                      columns = 2;
                    }

                    return availableNumbers
                      .reduce((rows, num, idx) => {
                        if (idx % columns === 0) rows.push([num]);
                        else rows[rows.length - 1].push(num);
                        return rows;
                      }, [])
                      .map((group, rowIdx) => (
                        <tr
                          key={rowIdx}
                          className="border-b border-gray-200  transition"
                        >
                          {group.map((num, colIdx) => (
                            <td
                              key={colIdx}
                              className={`p-4 ${
                                colIdx < columns - 1
                                  ? "border-r border-gray-200"
                                  : ""
                              } ${
                                columns === 1
                                  ? "w-full"
                                  : columns === 2
                                  ? "w-1/2"
                                  : "w-1/3"
                              }`}
                            >
                              <div className="flex justify-between items-center">
                                <span className="font-medium text-gray-800">
                                  {formatPhoneNumber(num.phone_number)}
                                </span>

                                <button
                                  onClick={redirectToUrl}
                                  className="bg-[#F6A33C] text-white px-3 py-1 text-sm rounded-lg hover:opacity-90 shadow-sm transition"
                                >
                                  Proceed
                                </button>
                              </div>
                            </td>
                          ))}
                        </tr>
                      ));
                  })()
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MainPage;
